/**
 * Utility functions for date and time handling
 * Simple timezone conversion between local time and UTC
 * 
 * ВАЖЛИВО: Backend Node.js працює з TZ=UTC для консистентності
 * PostgreSQL TIMESTAMP (without timezone) парсується pg driver як UTC завдяки TZ=UTC
 * Backend може повертати datetime БЕЗ 'Z' suffix, тому додаємо його на frontend
 */

/**
 * Ensures datetime string from backend has 'Z' suffix (treats as UTC)
 * Backend uses TIMESTAMP (without timezone) which may return datetime WITHOUT 'Z' suffix
 * @param datetimeString - Datetime string from backend (may or may not have 'Z' or timezone offset)
 * @returns ISO string with 'Z' to ensure UTC interpretation
 */
const ensureUTC = (datetimeString: string): string => {
  if (!datetimeString) return '';
  
  // If already ends with 'Z', it's already UTC
  if (datetimeString.endsWith('Z')) return datetimeString;
  
  // If has timezone offset (e.g., +00:00, -05:00), parse as-is (Date constructor handles it)
  if (/[+-]\d{2}:\d{2}$/.test(datetimeString)) return datetimeString;
  
  // Otherwise, add 'Z' to treat as UTC
  return datetimeString + 'Z';
};

/**
 * Converts UTC ISO string to local time for display
 * @param utcISOString - UTC ISO string from backend (may not have 'Z' due to TIMESTAMP column)
 * @returns Date object in local timezone
 */
export const utcToLocal = (utcISOString: string): Date => {
  if (!utcISOString) return new Date();
  return new Date(ensureUTC(utcISOString));
};

/**
 * Converts local time to UTC ISO string for backend storage
 * @param localDate - Local Date object or ISO string
 * @returns UTC ISO string for backend
 */
export const localToUTC = (localDate: Date | string): string => {
  const date = localDate instanceof Date ? localDate : new Date(localDate);
  return date.toISOString();
};

/**
 * Converts UTC ISO string to local datetime for input display
 * @param utcISOString - UTC ISO string from backend
 * @returns Formatted string for datetime-local input (YYYY-MM-DDTHH:MM)
 */
export const utcToLocalForInput = (utcISOString: string): string => {
  if (!utcISOString) return '';
  const localDate = utcToLocal(utcISOString);
  return localDate.toLocaleString('sv-SE').slice(0, 16);
};

/**
 * Converts local datetime input to UTC ISO string for backend
 * @param localInputString - Local datetime string from input (YYYY-MM-DDTHH:MM)
 * @returns UTC ISO string for backend
 */
export const localInputToUTC = (localInputString: string): string => {
  if (!localInputString) return '';
  const localDate = new Date(localInputString);
  return localToUTC(localDate);
};

/**
 * Moves a calendar day by a number of days, without any timezone conversion
 * @param date - Calendar day (YYYY-MM-DD)
 * @param days - Days to add (negative goes back)
 * @returns Calendar day (YYYY-MM-DD)
 */
export const addDaysToDate = (date: string, days: number): string => {
  const moved = new Date(`${date}T00:00:00.000Z`);
  moved.setUTCDate(moved.getUTCDate() + days);
  return moved.toISOString().slice(0, 10);
};

/**
 * Formats a Date as the local calendar day for a `<input type="date">` value
 * @param date - Date to format (defaults to now)
 * @returns Local calendar day as YYYY-MM-DD
 */
export const toLocalDateInput = (date: Date = new Date()): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

/**
 * Converts a local calendar day (from `<input type="date">`) to the UTC instant
 * at which that day starts in the user's timezone
 * @param localDateString - Local calendar day (YYYY-MM-DD)
 * @returns UTC ISO string for backend
 */
export const localDateStartToUTC = (localDateString: string): string => {
  if (!localDateString) return '';
  return localToUTC(new Date(`${localDateString}T00:00:00`));
};

/**
 * Converts a local calendar day (from `<input type="date">`) to the UTC instant
 * at which that day ends in the user's timezone, so the day is included in full
 * when used as an inclusive upper bound
 * @param localDateString - Local calendar day (YYYY-MM-DD)
 * @returns UTC ISO string for backend
 */
export const localDateEndToUTC = (localDateString: string): string => {
  if (!localDateString) return '';
  return localToUTC(new Date(`${localDateString}T23:59:59.999`));
};

/**
 * Formats a date string for display in UI (converts UTC to local time)
 * @param dateString - ISO date string (UTC from backend)
 * @returns Formatted date string for display in local timezone
 */
export const formatDate = (dateString: string): string => {
  const localDate = utcToLocal(dateString);
  return localDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Formats a date string for short display in UI (converts UTC to local time)
 * @param dateString - ISO date string (UTC from backend)
 * @returns Short formatted date string for display in local timezone (e.g., "Nov 15, 2:00 AM")
 */
export const formatShortDate = (dateString: string): string => {
  if (!dateString) return '';
  const localDate = utcToLocal(dateString);
  return localDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Parses a backend datetime to a valid Date, or null when absent/unparseable
 *
 * Prefer this over utcToLocal() when the caller must distinguish "no value" from a
 * real instant: utcToLocal() returns *now* for empty input, which silently renders a
 * plausible-looking wrong answer.
 * @param dateString - ISO date string (UTC from backend)
 */
const toLocalDate = (dateString: string | null | undefined): Date | null => {
  if (!dateString) return null;
  const localDate = utcToLocal(dateString);
  return Number.isNaN(localDate.getTime()) ? null : localDate;
};

/**
 * Formats an *instant* as a date, converting UTC to the viewer's timezone
 *
 * For timestamp fields (`@db.Timestamp`) where the time component is not displayed.
 * The conversion still matters: rendering the raw string would shift the calendar day
 * whenever the stored instant falls on the far side of the local date boundary (e.g.
 * an evening event stored as the next UTC day).
 *
 * For a calendar-day field (`@db.Date`) use formatCalendarDate instead.
 * @param dateString - ISO date string (UTC from backend)
 * @param fallback - Returned when there is no usable value
 * @returns Locale date string in the viewer's timezone (e.g. "8/27/2026")
 */
export const formatDateOnly = (
  dateString: string | null | undefined,
  fallback = 'N/A'
): string => {
  const localDate = toLocalDate(dateString);
  return localDate ? localDate.toLocaleDateString('en-US') : fallback;
};

/**
 * Formats a *calendar day* without any timezone conversion
 *
 * `@db.Date` columns (publish_date, start_date, end_date, target_date,
 * completed_date, …) carry no time of day and arrive as UTC midnight. Converting
 * them to a behind-UTC viewer zone rolls the day back, so the date parts are read
 * in UTC and the day is preserved exactly as stored.
 * @param dateString - ISO date string for a date-only column
 * @param fallback - Returned when there is no usable value
 * @returns Date string as stored (e.g. "8/27/2026")
 */
export const formatCalendarDate = (
  dateString: string | null | undefined,
  fallback = 'N/A'
): string => {
  if (!dateString) return fallback;
  const date = new Date(ensureUTC(dateString));
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString('en-US', { timeZone: 'UTC' });
};

/**
 * Formats a *calendar day* with an abbreviated month, without timezone conversion
 * @param dateString - ISO date string for a date-only column
 * @param fallback - Returned when there is no usable value
 * @returns Date string as stored (e.g. "Aug 27, 2026")
 */
export const formatMediumCalendarDate = (
  dateString: string | null | undefined,
  fallback = 'N/A'
): string => {
  if (!dateString) return fallback;
  const date = new Date(ensureUTC(dateString));
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  });
};

/**
 * Formats an *instant* as a date with an abbreviated month (converts UTC to local)
 * @param dateString - ISO date string (UTC from backend)
 * @param fallback - Returned when there is no usable value
 * @returns Locale date string in the viewer's timezone (e.g. "Aug 27, 2026")
 */
export const formatMediumDate = (
  dateString: string | null | undefined,
  fallback = 'N/A'
): string => {
  const localDate = toLocalDate(dateString);
  if (!localDate) return fallback;
  return localDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * True when both instants fall on the same calendar day in the viewer's timezone
 * @param dateString - ISO date string (UTC from backend)
 * @param other - Instant to compare against (defaults to now)
 */
export const isSameLocalDay = (dateString: string, other: Date = new Date()): boolean => {
  const localDate = toLocalDate(dateString);
  if (!localDate) return false;
  return localDate.toDateString() === other.toDateString();
};

/**
 * Formats an *instant* as an abbreviated date plus time (converts UTC to local time)
 *
 * Keeps the year, unlike formatShortDate — for lists where older entries accumulate
 * and a month/day alone would be ambiguous across years.
 * @param dateString - ISO date string (UTC from backend)
 * @param fallback - Returned when there is no usable value
 * @returns e.g. "Aug 27, 2026, 04:30 PM"
 */
export const formatMediumDateTime = (
  dateString: string | null | undefined,
  fallback = 'N/A'
): string => {
  const localDate = toLocalDate(dateString);
  if (!localDate) return fallback;
  return localDate.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Formats a time-only value for display (converts UTC to local time)
 *
 * Pinned to 'en-US' for consistency with formatDate/formatShortDate above; the app is
 * English-only, so a fixed locale keeps timestamps uniform across every view.
 * @param dateString - ISO date string (UTC from backend)
 * @param options - Overrides the default hour/minute parts (e.g. to include seconds)
 * @returns Locale time string in the viewer's timezone (e.g. "09:30 AM")
 */
export const formatTimeOnly = (
  dateString: string | null | undefined,
  options: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' }
): string => {
  const localDate = toLocalDate(dateString);
  if (!localDate) return '';
  return localDate.toLocaleTimeString('en-US', options);
};

/**
 * Formats a chat/notification timestamp: time for today, abbreviated date otherwise
 * @param dateString - ISO date string (UTC from backend)
 * @returns e.g. "09:30 AM" for today, "Aug 27" for any earlier day
 */
export const formatRelativeDayTime = (dateString: string | null | undefined): string => {
  const localDate = toLocalDate(dateString);
  if (!localDate) return '';
  if (isSameLocalDay(localDate.toISOString())) {
    return localDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  }
  return localDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * Formats a short day name for suggestion cards (converts UTC to local time)
 * @param dateString - ISO date string (UTC from backend)
 * @returns Short weekday name in local timezone
 */
export const formatSuggestionDay = (dateString: string): string => {
  const localDate = utcToLocal(dateString);
  return localDate.toLocaleDateString('en-US', { weekday: 'short' });
};

/**
 * Gets the next available hour from current time
 * @returns UTC ISO string for the next hour (rounded to minutes)
 */
export const getNextAvailableHour = (): string => {
  const now = new Date();
  now.setHours(now.getHours() + 1, 0, 0, 0); // Next hour, rounded to minutes
  return localToUTC(now);
};

/**
 * Gets date constraints for meeting scheduling
 * @param targetDate - Contract target date (UTC ISO string) - not used for max constraint anymore
 * @returns Object with minDate and maxDate (both in local time)
 */
export const getDateConstraints = (targetDate?: string) => {
  const now = new Date();
  
  // Minimum date: current time (not in the past) - already local
  const minDate = now;
  
  // Maximum date: far in the future (5 years) to allow any future date selection
  const maxDate = new Date(now.getTime() + (5 * 365 * 24 * 60 * 60 * 1000)); // 5 years
  
  return { minDate, maxDate };
};

/**
 * Validates if a date is within the given constraints
 * @param date - Date string to validate
 * @param minDate - Minimum allowed date
 * @param maxDate - Maximum allowed date
 * @returns True if date is within constraints
 */
export const isDateWithinConstraints = (
  date: string,
  minDate: Date,
  maxDate: Date
): boolean => {
  if (!date) return false;
  const selectedDateTime = new Date(date);
  return selectedDateTime >= minDate && selectedDateTime <= maxDate;
};

/**
 * Adds business days to a date (excluding weekends)
 * @param date - Starting date (ISO string or Date)
 * @param days - Number of business days to add
 * @returns ISO string of the resulting date
 */
export const addBusinessDays = (date: string | Date, days: number): string => {
  let currentDate = new Date(date);
  let addedDays = 0;
  
  while (addedDays < days) {
    currentDate.setDate(currentDate.getDate() + 1);
    // Skip weekends (0 = Sunday, 6 = Saturday)
    if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
      addedDays++;
    }
  }
  
  return currentDate.toISOString();
};
